import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ContractService } from '@/services/contract/contractService';
import { Contract, CreateContractRequest, DeleteContractFileRequest, AddContractFileRequest, RequestContractSignatureRequest, MarkContractSignatureRequest } from '@/types/payment';
import { FileObject } from '@/types/shared/file';

export const useContract = () => {
  const queryClient = useQueryClient();

  // Get all contracts
  const useGetAllContracts = () => {
    return useQuery({
      queryKey: ['contracts'],
      queryFn: () => ContractService.getAllContracts(),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get single contract
  const useGetContract = (id: string) => {
    return useQuery({
      queryKey: ['contract', id],
      queryFn: () => ContractService.getContract(id),
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    });
  };

  // Get contracts by payment ID
  const useGetContractsByPaymentId = (paymentId: string) => {
    return useQuery({
      queryKey: ['contracts', 'payment', paymentId],
      queryFn: () => ContractService.getContractsByPaymentId(paymentId),
      enabled: !!paymentId,
      staleTime: 5 * 60 * 1000,
    });
  };


  // Get all contracts extended with listing and payment info
  const useGetAllContractsExtended = (page: number = 1, limit: number = 10, status?: string, search?: string) => {
    return useQuery({
      queryKey: ['contracts', 'extended', page, limit, status, search],
      queryFn: () => ContractService.getAllContractsExtended(page, limit, status, search),
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
    });
  };

  // Create contract mutation
  const createContractMutation = useMutation({
    mutationFn: ({ request, replaceExisting }: { request: CreateContractRequest; replaceExisting?: boolean }) =>
      ContractService.createContract(request, replaceExisting),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contracts', 'extended'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-card'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-cards'] });
    },
  });

  // Update contract mutation
  const updateContractMutation = useMutation({
    mutationFn: (data: Partial<Contract> & { id: string }) =>
      ContractService.updateContract(data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Delete contract mutation
  const deleteContractMutation = useMutation({
    mutationFn: (id: string) => ContractService.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Delete contract file mutation
  const deleteContractFileMutation = useMutation({
    mutationFn: (request: DeleteContractFileRequest) => ContractService.deleteContractFile(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Add contract file mutation
  const addContractFileMutation = useMutation({
    mutationFn: (request: AddContractFileRequest) => ContractService.addContractFile(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  const requestSignatureMutation = useMutation({
    mutationFn: (request: RequestContractSignatureRequest) => ContractService.requestSignature(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contracts', 'extended'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  const markSignatureMutation = useMutation({
    mutationFn: (request: MarkContractSignatureRequest) => ContractService.markSignature(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contracts', 'extended'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Check existing contract by listing ID
  const checkExistingContractByListingId = async (listingId: string) => {
    return await ContractService.checkExistingContractByListingId(listingId);
  };

  const getBankIdSignatureStartUrl = (contractId: string) =>
    ContractService.getBankIdSignatureStartUrl(contractId);

  return {
    // Queries
    useGetAllContracts,
    useGetContract,
    useGetContractsByPaymentId,
    useGetAllContractsExtended,
    
    // Utility functions
    checkExistingContractByListingId,
    getBankIdSignatureStartUrl,
    
    // Mutations
    createContract: createContractMutation.mutate,
    createContractAsync: createContractMutation.mutateAsync,
    isCreatingContract: createContractMutation.isPending,
    createContractError: createContractMutation.error,
    resetCreateContract: createContractMutation.reset,
    
    updateContract: updateContractMutation.mutate,
    updateContractAsync: updateContractMutation.mutateAsync,
    isUpdatingContract: updateContractMutation.isPending,
    updateContractError: updateContractMutation.error,
    resetUpdateContract: updateContractMutation.reset,
    
    deleteContract: deleteContractMutation.mutate,
    deleteContractAsync: deleteContractMutation.mutateAsync,
    isDeletingContract: deleteContractMutation.isPending,
    deleteContractError: deleteContractMutation.error,
    resetDeleteContract: deleteContractMutation.reset,
    
    // File operations
    deleteContractFile: deleteContractFileMutation.mutate,
    deleteContractFileAsync: deleteContractFileMutation.mutateAsync,
    isDeletingContractFile: deleteContractFileMutation.isPending,
    deleteContractFileError: deleteContractFileMutation.error,
    resetDeleteContractFile: deleteContractFileMutation.reset,
    
    addContractFile: addContractFileMutation.mutate,
    addContractFileAsync: addContractFileMutation.mutateAsync,
    isAddingContractFile: addContractFileMutation.isPending,
    addContractFileError: addContractFileMutation.error,
    resetAddContractFile: addContractFileMutation.reset,

    requestSignature: requestSignatureMutation.mutate,
    requestSignatureAsync: requestSignatureMutation.mutateAsync,
    isRequestingSignature: requestSignatureMutation.isPending,
    requestSignatureError: requestSignatureMutation.error,

    markSignature: markSignatureMutation.mutate,
    markSignatureAsync: markSignatureMutation.mutateAsync,
    isMarkingSignature: markSignatureMutation.isPending,
    markSignatureError: markSignatureMutation.error,
  };
};
